import React from 'react';
import '../css/OurPartners.css';
import { ExternalLink, Handshake, Award, Globe } from 'lucide-react';

interface Partner {
  name: string;
  type: string;
  description: React.ReactNode;
  logo?: string;
  website: string;
  partnership: string;
}

const OurPartners: React.FC = () => {
  const partners: Partner[] = [
    {
      name: 'Acme Insurance Group',
      type: 'Strategic Partner',
      description: <span>Leading insurance provider leveraging Crikey<sup>ai</sup> to transform their claims processing workflow and improve customer satisfaction.</span>,
      website: 'https://acmeinsurance.com',
      partnership: 'Enterprise Integration Partner'
    },
    {
      name: 'TechVision Solutions',
      type: 'Technology Partner',
      description: <span>Cloud infrastructure and integration specialists helping enterprises deploy Crikey<sup>ai</sup> at scale with seamless system integration.</span>,
      website: 'https://techvision.com',
      partnership: 'Cloud & Infrastructure Partner'
    },
    {
      name: 'DataFlow Analytics',
      type: 'Technology Partner',
      description: <span>Advanced analytics platform providing deep insights and business intelligence integration with Crikey<sup>ai</sup> for enhanced decision-making.</span>,
      website: 'https://dataflow.com',
      partnership: 'Analytics Integration Partner'
    },
    {
      name: 'GlobalShield Insurance',
      type: 'Strategic Partner',
      description: <span>International insurance leader using Crikey<sup>ai</sup> to process over 50,000 claims monthly across multiple regions with 98% accuracy.</span>,
      website: 'https://globalshield.com',
      partnership: 'Global Implementation Partner'
    },
    {
      name: 'SecureCloud Systems',
      type: 'Technology Partner',
      description: <span>Cybersecurity and compliance specialists ensuring Crikey<sup>ai</sup> deployments meet the highest security standards and regulatory requirements.</span>,
      website: 'https://securecloud.com',
      partnership: 'Security & Compliance Partner'
    },
    {
      name: 'InsurTech Innovations',
      type: 'Strategic Partner',
      description: <span>Insurance technology consultancy helping organizations optimize their AI adoption strategy and maximize ROI with Crikey<sup>ai</sup>.</span>,
      website: 'https://insurtech.com',
      partnership: 'Consulting & Advisory Partner'
    }
  ];

  const partnershipBenefits = [
    {
      icon: <Handshake size={32} />,
      title: 'Collaborative Growth',
      description: 'Work alongside industry leaders to shape the future of AI-powered insurance claims processing.'
    },
    {
      icon: <Award size={32} />,
      title: 'Co-Marketing Opportunities',
      description: 'Leverage joint marketing initiatives, case studies, and speaking opportunities to expand your reach.'
    },
    {
      icon: <Globe size={32} />,
      title: 'Global Network',
      description: 'Access our worldwide network of insurance professionals and technology experts.'
    }
  ];

  return (
    <div className="partners-section">
      <div className="partners-content">
        <h2 className="partners-title">Our Partners</h2>
        <p className="partners-subtitle">
          Building the future of insurance technology together with industry-leading organizations.
        </p>

        {/* Partnership Benefits */}
        <div className="benefits-section">
          <h3 className="benefits-title">Why Partner With Us</h3>
          <div className="benefits-grid">
            {partnershipBenefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4 className="benefit-title">{benefit.title}</h4>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Partners Grid */}
        <div className="partners-grid">
          {partners.map((partner, index) => (
            <div key={index} className="partner-card">
              <div className="partner-header">
                <div className="partner-type-badge">{partner.type}</div>
                <h3 className="partner-name">{partner.name}</h3>
                <p className="partnership-type">{partner.partnership}</p>
              </div>

              <div className="partner-body">
                <p className="partner-description">{partner.description}</p>
              </div>

              <div className="partner-footer">
                <a 
                  href={partner.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="partner-link"
                >
                  <span>Visit Website</span>
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="partner-cta-section">
          <h3 className="cta-title">Interested in Becoming a Partner?</h3>
          <p className="cta-description">
            Join our growing ecosystem of technology and insurance leaders. Let's transform the industry together.
          </p>
          <div className="cta-buttons">
            <a href="/contact" className="cta-button primary cta-button-mobile">
              Become a Partner
            </a>
            <a href="/partnerships-info.pdf" className="cta-button secondary cta-button-mobile">
              Download Partnership Info
            </a>
          </div>
        </div>

        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-item">
            <h4 className="stat-number">50+</h4>
            <p className="stat-label">Global Partners</p>
          </div>
          <div className="stat-item">
            <h4 className="stat-number">30+</h4>
            <p className="stat-label">Countries Reached</p>
          </div>
          <div className="stat-item">
            <h4 className="stat-number">1M+</h4>
            <p className="stat-label">Claims Processed</p>
          </div>
          <div className="stat-item">
            <h4 className="stat-number">98%</h4>
            <p className="stat-label">Partner Satisfaction</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurPartners;